/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";
import {
  PhoneCall,
  MessageCircle,
  Mail,
  Calendar,
  Loader2,
  MapPin,
} from "lucide-react";
import React, { useState, useEffect } from "react";

import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { SendEmailModal } from "@/app/(authenticated)/communication/_modules/components/SendEmailModal";
import { SendSmsModal } from "@/app/(authenticated)/communication/_modules/components/SendSmsModal";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { useLeadCall } from "@/app/(authenticated)/leads/_modules/hooks/useLeadCall";
import { NewReminderDialog } from "@/app/(authenticated)/reminders/_modules/components/NewReminderDialog";
import { Button } from "@/app/_module/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/app/_module/components/ui/dropdown-menu";
import api from "@/app/_module/services/api";
import { useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/app/_module/services/config";
import { toast } from "@/app/_module/components/ui/use-toast";
import { formatPhoneNumber as formatPhoneNumberForDisplay } from "@/app/_module/utils/formatters";
import ViewerDisabled from "@/app/_module/components/ViewerDisabled";
import { useSmsConversation } from "@/app/_module/contexts/SmsConversationContext";
import { leadsService } from "@/app/(authenticated)/leads/_modules/services/services";
import { useDoorKnockingPreference } from "@/app/_module/hooks/useDoorKnockingPreference";

interface LeadActionButtonsProps {
  lead: Lead;
  onEdit: () => void;
  onCallInitiated?: (callData: { callId: string; status: string; phoneNumber: string }) => void;
  onSmsOpen?: () => void;
  hideCallButton?: boolean; // When true, hides the Call button (for Power Dialer context)
  onStatusChange?: (leadId: string, newStatus: string) => void; // Callback when lead status changes (for tracking)
  // View-only: the lead was shared with this user (e.g. via an @mention) but
  // belongs to another agent. They can see it but not act on it — call/text/
  // email/schedule are disabled so it stays the owning agent's lead.
  viewOnly?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LeadActionButtons = function ({ lead, onEdit, onCallInitiated, onSmsOpen, hideCallButton = false, onStatusChange, viewOnly = false }: LeadActionButtonsProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showScheduleOptions, setShowScheduleOptions] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { callLead, isInitiatingCall } = useLeadCall(lead, onCallInitiated);

  const { doorKnockingEnabled } = useDoorKnockingPreference();
  const DOOR_KNOCK_TAG = "door_knock";
  const isDoorKnocking =
    String((lead as any)?.listName ?? "").trim().toLowerCase() === "door knocking" ||
    (Array.isArray((lead as any)?.tags) && (lead as any).tags.map((t: string) => String(t).toLowerCase()).includes(DOOR_KNOCK_TAG));
  const queryClient = useQueryClient();
  const [doorKnocked, setDoorKnocked] = useState(isDoorKnocking);
  useEffect(() => { setDoorKnocked(isDoorKnocking); }, [isDoorKnocking]);
  const { activeSmsConversation, startConversation, maximizeConversation } = useSmsConversation();




  const handleSMS = async () => {
    // Check if there's already an active SMS conversation with this lead in memory
    if (activeSmsConversation && activeSmsConversation.phoneNumber === lead.phone) {
      // If conversation exists in memory, just maximize/show the widget
      maximizeConversation();
      return;
    }

    // No active conversation in memory - check if there's SMS history in the database
    // If history exists, open widget directly with that history
    // If no history, open modal to send first message
    try {
      const { getSmsHistoryForPhone } = await import('@/app/(authenticated)/communication/_modules/services/smsHistoryService');
      const historyMessages = await getSmsHistoryForPhone(lead.phone!);

      if (historyMessages.length > 0) {
        // History exists - open widget directly with full conversation
        await startConversation({
          leadId: lead.id,
          leadName: lead.name,
          phoneNumber: lead.phone!,
          // No initial message - just loading existing history
        });
      } else {
        // No history - open modal to send first message
        setShowSmsModal(true);
      }
    } catch (error) {
      // Fallback to modal on error
      setShowSmsModal(true);
    }
  };

  const handleEmail = () => {
    setShowEmailModal(true);
  };

  // Extract first name from full name
  const getFirstName = (fullName: string) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  return (
    <React.Fragment>
      <div className={`grid gap-3 ${hideCallButton ? (doorKnockingEnabled ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3') : (doorKnockingEnabled ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4')}`}>
        {!hideCallButton && (
          <ViewerDisabled message="Upgrade to call this lead">
            <Button
              className="h-11 bg-green-600 hover:bg-green-700 w-full"
              onClick={() => callLead()}
              disabled={isInitiatingCall || !lead.phone || viewOnly}
            >
              {isInitiatingCall ? (
                <React.Fragment>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Calling...
                </React.Fragment>
              ) : (
                // Last-call recency INSIDE the Call button — the info lives
                // exactly where the double-dial decision happens (Enes' request)
                <div className="flex flex-col items-center leading-tight">
                  <span className="flex items-center">
                    <PhoneCall className="h-4 w-4 mr-2" />
                    Call {getFirstName(lead.name)}
                  </span>
                  <span className="text-[10px] font-normal text-white/80">
                    {(() => {
                      const lc = (lead as any)?.lastCall;
                      if (!lc) return 'Never called';
                      const d = new Date(lc);
                      const sameYear = d.getFullYear() === new Date().getFullYear();
                      return `Last: ${d.toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric',
                        ...(sameYear ? { hour: 'numeric', minute: '2-digit' } : { year: 'numeric' }),
                      })}`;
                    })()}
                  </span>
                </div>
              )}
            </Button>
          </ViewerDisabled>
        )}
        <ViewerDisabled message="Upgrade to text this lead">
          <Button
            className="h-11 border-primary/20 hover:bg-primary/5 w-full"
            disabled={!lead.phone || viewOnly}
            onClick={handleSMS}
            variant="outline"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Send SMS
          </Button>
        </ViewerDisabled>
        <ViewerDisabled message="Upgrade to email this lead">
          <Button
            className="h-11 border-purple-200 hover:bg-purple-50 w-full"
            disabled={!lead.email || viewOnly}
            onClick={handleEmail}
            variant="outline"
          >
            <Mail className="h-4 w-4 mr-2" />
            Send Email
          </Button>
        </ViewerDisabled>
        <ViewerDisabled message="Upgrade to schedule appointments">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-11 border-orange-200 hover:bg-orange-50 w-full"
                variant="outline"
                disabled={viewOnly}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-68">
              <DropdownMenuItem
                onClick={() => {
                  setTimeout(() => {
                    setShowAppointmentDialog(true);
                  }, 100);
                }}
              >
                Create Appointment
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => {
                  setTimeout(() => {
                    setShowReminderDialog(true);
                  }, 100);
                }}
              >
                Create Reminder
              </DropdownMenuItem>

            </DropdownMenuContent>
          </DropdownMenu>
        </ViewerDisabled>
        {doorKnockingEnabled && (
          <ViewerDisabled message="Upgrade to use door knocking">
            <Button
              className={`h-11 w-full ${doorKnocked ? "border-gray-200 text-gray-400 bg-gray-50" : "border-amber-200 hover:bg-amber-50"}`}
              variant="outline"
              disabled={doorKnocked}
              title={doorKnocked ? "Already marked as Door Knocking" : undefined}
              onClick={async () => {
                try {
                  // Source AND tag: the source is what the routes screen filters on,
                  // the tag is what shows on the lead and in tag filters.
                  await Promise.all([
                    leadsService.updateLead(lead.id, { listName: "Door Knocking" }),
                    api.post({ url: getApiUrl("/leads/bulk-add-tag"), body: { leadIds: [lead.id], tag: DOOR_KNOCK_TAG }, auth: true }),
                  ]);
                  setDoorKnocked(true);
                  void queryClient.invalidateQueries({ queryKey: ["leads"] });
                  toast({ title: "Lead marked as Door Knocking", description: "Source set and tagged door knock.", variant: "default" });
                } catch {
                  toast({ title: "Failed to update lead source", variant: "destructive" });
                }
              }}
            >
              <MapPin className="h-4 w-4 mr-2" />
              {doorKnocked ? "Door Knocking" : "Door Knock"}
            </Button>
          </ViewerDisabled>
        )}

      </div>

      {/* Appointment Dialog */}
      <NewAppointmentDialog
        onOpenChange={setShowAppointmentDialog}
        open={showAppointmentDialog}
        preselectedLeadId={lead.id}
        preselectedLeadName={lead.name}
        preselectedLeadState={lead.state}
        onSave={(apptData) => {
          //
        }}
        onStatusChange={onStatusChange}
      />

      {/* Reminder Dialog - preselects this lead for association */}
      <NewReminderDialog
        editingReminder={undefined}
        onOpenChange={setShowReminderDialog}
        open={showReminderDialog}
        preselectedLeadId={lead.id}
        preselectedLeadName={lead.name}
      />

      <SendSmsModal
        lead={lead} // pass the loaded lead object from the page
        onOpenChange={setShowSmsModal}
        open={showSmsModal}
      />

      <SendEmailModal
        lead={lead} // pass the loaded lead object from the page
        onOpenChange={setShowEmailModal}
        open={showEmailModal}
      />
    </React.Fragment>
  );
}
